import React, { Dispatch } from "react";
import { Button } from "reactstrap";
import { WorkflowEditor, PermissionsAssigner, Textarea, Input } from "cm-workflow";
import { isError } from "util";
import { connect } from "react-redux";
import { Input as UICoreInput } from "@cadmakers-ui/core";
import { Workflow } from "../../Models/Workflow";
import { workflowEditorConstants } from "../../Constants/WorkflowEditorConstants";
import { ApiRequest, LoadingState, ErrorDisplay, LoadingDisplay, IAppState } from "../../../Common";
import { endpoints } from "../..";
import styles from "../../Styles/WorkflowEditorWrapper.module.scss";
import { templateNormal, templateSmall, linkTemplate } from "../../Constants/workflowTemplates";
import WorkflowImagePicker from "./WorkflowImagePicker";
import { WorkflowTriggerSelector } from "./WorkflowTriggerSelector";
import { workflowDefaultPermissionsService } from "../../Services/WorkflowDefaultPermissionsService";
import { IconSet } from "../../Models/IconSet";
import { getWorkflowImageSets, setLoading } from "../../Actions/WorkflowImagesActions";
import { Trigger } from "../../Models/Trigger";
import { load } from "../../Reducers/TriggerReducer";

type Props = {
  workflow: Workflow;
  permissions: string[];
  teams: string[];
  isLoading: boolean;
  onSave: (json: string) => void;
  onCancel: () => void;
};

type StateProps = {
  // workflow images stuff
  workflowImageSets: IconSet[];
  workflowImagesLoadingState: LoadingState;
  workflowImagesErrorMessage: string;
  workflowImagesErrorCode?: number;

  // trigger related data
  triggers: Trigger[];
  triggerLoadingState: LoadingState;
};

type DispatchProps = {
  getWorkflowImageSets: () => void;
  setWorkflowImageSetsLoading: () => void;
  loadTriggers: () => void;
};

type State = {
  loadedJson?: string;
  loadingState: LoadingState;
  errorMessage?: string;
  isSaving: boolean;
};

const mapStateToProps = (state: IAppState): StateProps => {
  return {
    workflowImageSets: state.workflowImages.imageSets,
    workflowImagesLoadingState: state.workflowImages.loadingState,
    workflowImagesErrorMessage: state.workflowImages.errorMessage,
    workflowImagesErrorCode: state.workflowImages.errorCode,
    triggers: state.triggers.triggers,
    triggerLoadingState: state.triggers.loadingState
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  getWorkflowImageSets: () => dispatch(getWorkflowImageSets({})),
  setWorkflowImageSetsLoading: () => dispatch(setLoading({})),
  loadTriggers: () => load()(dispatch)
});

class WorkflowEditorWrapper extends React.Component<Props & StateProps & DispatchProps, State> {
  constructor(props: Props & StateProps & DispatchProps) {
    super(props);

    this.state = {
      loadingState: { isLoaded: false, isLoading: false, errored: false },
      isSaving: false
    };
  }

  componentDidMount() {
    // start loading the json
    this.setState(
      prevState => ({
        loadingState: {
          ...prevState.loadingState,
          isLoading: true
        }
      }),
      () => {
        this.loadJsonForWorkflow();
      }
    );

    // start loading images
    if (
      !this.props.workflowImagesLoadingState.isLoading &&
      !this.props.workflowImagesLoadingState.errored &&
      !this.props.workflowImagesLoadingState.isLoaded
    ) {
      this.props.setWorkflowImageSetsLoading();
      this.props.getWorkflowImageSets();
    }

    if (
      !this.props.triggerLoadingState.isLoading &&
      !this.props.triggerLoadingState.errored &&
      !this.props.triggerLoadingState.isLoaded
    ) {
      this.props.loadTriggers();
    }
  }

  renderInput = (
    value: any,
    readOnly: any,
    targetKey: any,
    partType: any,
    partCategory: any,
    model: any,
    isValid: any,
    renderProps: any,
    onChange: any
  ) => {
    return (
      <Input
        value={value}
        onChange={onChange}
        {...renderProps}
        disabled={readOnly}
        isValid={isValid}
      />
    );
  };

  renderSwitch = (
    value: any,
    readOnly: any,
    targetKey: any,
    partType: any,
    partCategory: any,
    model: any,
    isValid: any,
    renderProps: any,
    onChange: any
  ) => {
    return (
      <UICoreInput checked={value} onChange={() => onChange(!value)} type="switch" translate={{}} />
    );
  };

  renderTriggerSelector = (
    value: any,
    readOnly: any,
    targetKey: any,
    partType: any,
    partCategory: any,
    model: any,
    isValid: any,
    renderProps: any,
    onChange: any
  ) => {
    return (
      <WorkflowTriggerSelector
        selectedTriggers={value === null ? [] : value}
        availableTriggers={this.props.triggers}
        disabled={false}
        onChange={onChange}
      />
    );
  };

  renderTextarea = (
    value: any,
    readOnly: any,
    targetKey: any,
    partType: any,
    partCategory: any,
    model: any,
    isValid: any,
    renderProps: any,
    onChange: any
  ) => {
    return <Textarea value={value} onChange={onChange} {...renderProps} disabled={readOnly} />;
  };

  renderIcon = (
    value: any,
    readOnly: any,
    targetKey: any,
    partType: any,
    partCategory: any,
    model: any,
    isValid: any,
    renderProps: any,
    onChange: any
  ) => {
    return (
      <WorkflowImagePicker
        icon={value}
        onIconPicked={onChange}
        disabled={readOnly}
        isValid={isValid}
        workflowImageSets={this.props.workflowImageSets}
        loadingState={this.props.workflowImagesLoadingState}
        errorMessage={this.props.workflowImagesErrorMessage}
        errorCode={this.props.workflowImagesErrorCode}
      />
    );
  };

  renderPermissionsAssigner = (
    value: any,
    readOnly: any,
    targetKey: any,
    partType: any,
    partCategory: any,
    model: any,
    isValid: any,
    renderProps: any,
    onChange: any
  ) => {
    return (
      <PermissionsAssigner
        value={value}
        onChange={onChange}
        disabled={readOnly}
        permissions={value}
      />
    );
  };

  getStatusNodeProperties = () => {
    return [
      {
        propertyName: "title",
        label: "Title",
        render: this.renderInput,
        renderProps: {
          placeholder: "Add Title..."
        },
        defaultValue: "New State",
        order: -2,
        validator: (...args: any) => true
      },
      {
        propertyName: "icon",
        label: "Icon",
        render: this.renderIcon,
        defaultValue: {},
        order: 1,
        validator: (...args: any) => true
      },
      {
        propertyName: "description",
        label: "Description",
        render: this.renderTextarea,
        renderProps: {
          placeholder: "Add Description..."
        },
        defaultValue: "",
        order: 2
      },
      {
        propertyName: "permissions",
        label: "Permissions",
        render: this.renderPermissionsAssigner,
        defaultValue: this.props.permissions.map(p => {
          return {
            name: p,
            roles: workflowDefaultPermissionsService.getDefaultAssignedRoles(p, this.props.teams)
          };
        }),
        order: 3
      },
      {
        propertyName: "isClosed",
        label: "Closed",
        render: this.renderSwitch,
        defaultValue: false,
        order: 4
      }
    ];
  };

  getLinkProperties = () => {
    return [
      {
        propertyName: "label",
        label: "Label",
        render: this.renderInput,
        renderProps: {
          placeholder: "Add Label..."
        },
        defaultValue: "",
        order: -2,
        validator: (...args: any) => true
      },
      {
        propertyName: "permissions",
        label: "Permissions",
        render: this.renderPermissionsAssigner,
        defaultValue: [
          {
            name: "Can Transition",
            roles: workflowDefaultPermissionsService.getDefaultAssignedRoles(
              "Can Transition",
              this.props.teams
            )
          }
        ],
        order: 3
      },
      {
        propertyName: "selectedTriggers",
        label: "Triggers",
        render: this.renderTriggerSelector,
        defaultValue: [],
        order: 4
      }
    ];
  };

  onModelChange = (jsonString: string) => {
    if (jsonString !== this.state.loadedJson) {
      this.setState((prevState: State) => ({
        loadedJson: jsonString
      }));
    }
  };

  onClickSave = () => {
    if (!this.state.isSaving) {
      this.setState(
        () => ({
          isSaving: true
        }),
        () => {
          if (this.state.loadedJson) {
            this.props.onSave(this.state.loadedJson);
          }
          this.setState(() => ({
            isSaving: false
          }));
        }
      );
    }
  };

  loadJsonForWorkflow() {
    ApiRequest.fromPath(endpoints.getJson(this.props.workflow.code))
      .withAuthHeader()
      .get()
      .then(response => response.json())
      .then(json => {
        if (json.status === undefined) {
          // this was successful
          // check if isClosed exists, if not, add to json (for every state)
          for (let i = 0; i < json.payload.nodeDataArray.length; i++) {
            // eslint-disable-next-line no-prototype-builtins
            if (!json.payload.nodeDataArray[i].hasOwnProperty("isClosed")) {
              // eslint-disable-next-line no-param-reassign
              json.payload.nodeDataArray[i].isClosed = false;
            }
          }
          for (let i = 0; i < json.payload.linkDataArray.length; i++) {
            // eslint-disable-next-line no-prototype-builtins
            if (!json.payload.linkDataArray[i].hasOwnProperty("selectedTriggers")) {
              // eslint-disable-next-line no-param-reassign
              json.payload.linkDataArray[i].selectedTriggers = [];
            }
          }
          this.setState((prevState: State) => ({
            loadingState: {
              isLoaded: true,
              isLoading: false,
              errored: false
            },
            loadedJson: JSON.stringify(json.payload)
          }));
        } else {
          this.setState((prevState: State) => ({
            loadingState: {
              isLoaded: false,
              isLoading: false,
              errored: true
            },
            errorMessage: json.error.message
          }));
        }
      });
  }

  render() {
    if (this.state.loadingState.errored) {
      return (
        <ErrorDisplay
          errorDescription={this.state.errorMessage}
          errorResult="Failed to load workflow JSON"
        />
      );
    }

    if (this.state.loadingState.isLoaded && !this.props.isLoading) {
      return (
        <>
          <div className={styles.workflowButtonWrapper}>
            <Button disabled={this.state.isSaving} onClick={this.onClickSave}>
              Save
            </Button>
          </div>
          <div className={styles.workflowEditorWrapper}>
            <WorkflowEditor
              // Avoid using this if possible!
              onRef={() => {}}
              isLoading={this.props.isLoading}
              title={this.props.workflow.name}
              model={this.state.loadedJson}
              gridOptions={{
                rows: workflowEditorConstants.grid.rows,
                cols: workflowEditorConstants.grid.cols
              }}
              onModelChange={this.onModelChange}
              onCancel={this.props.onCancel}
              roles={this.props.teams}
              permissions={this.props.permissions}
              disableAutosave
              // new stuff
              readOnly={false}
              // portConfig={{ singlePort: false, left: true, right: true, top: true, bottom: true }}
              renderLinkTooltip={() => ""}
              statusNodeTemplate={{
                normal: (width: number, height: number) => templateNormal(),
                small: (width: number, height: number) => templateSmall()
              }}
              linkLabelTemplate={() => linkTemplate()}
              statusNodeProperties={this.getStatusNodeProperties()}
              linkProperties={this.getLinkProperties()}
              nodeValidator={() => true}
              linkValidator={() => true}
              canAppendNode={() => true}
              canMakeLink={() => true}
              allowCycles
              disableTopbar
              portConfig={{ left: true, right: true, bottom: true, top: true }}
              onDataInspectorSave={() => {}}
              onDataInspectorOpen={() => {}}
            />
          </div>
        </>
      );
    }

    return <LoadingDisplay />;
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(WorkflowEditorWrapper);
